/**
 * DeliveryDateModal - Select a new delivery date
 */
import { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Alert,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QuizButton } from "./shared/QuizButton";
import { PawsLoader } from "./shared/PawsLoader";
import type { AvailableDeliveryDate } from "../api/accountProxy";
import { QUIZ } from "@/theme/quizTokens";

type DeliveryDateModalProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
  currentDeliveryDate?: string;
  availableDates: AvailableDeliveryDate[];
  loading?: boolean;
  loadingError?: string;
  onConfirm: (date: string) => Promise<void>;
  deliveryDayNames?: string[];
};

function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00Z");
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00Z");
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export function DeliveryDateModal({
  open,
  onClose,
  currentDeliveryDate,
  availableDates,
  loading = false,
  loadingError,
  onConfirm,
  deliveryDayNames,
}: DeliveryDateModalProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Track previous open state to only reset on actual modal open
  const prevOpenRef = useRef(false);

  // Pre-select current delivery date only when modal opens (not on data changes while open)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only reset when transitioning from closed to open
    if (open && !wasOpen) {
      setSelectedDate(currentDeliveryDate || null);
      setError(null);
      setSuccess(false);
      setConfirming(false);
    }
  }, [open, currentDeliveryDate]);

  // Group dates by month for display (include current date)
  const datesByMonth = useMemo(() => {
    const grouped: Record<string, AvailableDeliveryDate[]> = {};

    for (const d of availableDates) {
      const date = new Date(d.date + "T00:00:00Z");
      const monthKey = date.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });

      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(d);
    }

    return grouped;
  }, [availableDates]);

  const handleConfirm = async () => {
    if (!selectedDate || selectedDate === currentDeliveryDate) return;

    setConfirming(true);
    setError(null);

    try {
      await onConfirm(selectedDate);
      setSuccess(true);
      // Auto-close after success
      setTimeout(() => {
        onClose(true);
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to update delivery date");
    } finally {
      setConfirming(false);
    }
  };

  const selectedDateInfo = selectedDate
    ? availableDates.find((d) => d.date === selectedDate)
    : null;

  const hasChangedDate = selectedDate && selectedDate !== currentDeliveryDate;

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : () => onClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          maxHeight: { xs: "100%", sm: "85vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header - Fixed */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          borderBottom: "1px solid rgba(0,0,0,.08)",
          background: QUIZ.color.cream,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #E6F0FF 0%, #C9DFFF 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 22, color: "#1A57D3" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              Change Delivery Date
            </Typography>
            {currentDeliveryDate && (
              <Typography
                sx={{
                  fontSize: { xs: 12, md: 13 },
                  color: QUIZ.color.brown,
                  opacity: 0.65,
                }}
              >
                Current: {formatShortDate(currentDeliveryDate)}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton
          onClick={() => onClose(false)}
          disabled={confirming}
          sx={{
            color: QUIZ.color.brown,
            "&:hover": { background: "rgba(0,0,0,.05)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Success State */}
        {success && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            Delivery date updated successfully!
          </Alert>
        )}

        {/* Error State */}
        {(error || loadingError) && (
          <Alert
            severity="error"
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            {error || loadingError}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <PawsLoader
            action="delivery"
            fullScreen={false}
            size="small"
          />
        )}

        {/* Date Selection */}
        {!loading && !success && (
          <>
            <Alert
              severity="info"
              sx={{
                borderRadius: "12px",
                mb: 2,
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              This will update all your subscriptions (meals and treats) to the
              new delivery date.
            </Alert>

            <Typography
              sx={{
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.brown,
                opacity: 0.75,
                mb: 2,
              }}
            >
              Select a delivery date.{deliveryDayNames && deliveryDayNames.length > 0 && (
                <> Available on{" "}
                  <strong>
                    {deliveryDayNames.length === 1
                      ? deliveryDayNames[0] + "s"
                      : deliveryDayNames.length === 2
                      ? deliveryDayNames[0] + "s & " + deliveryDayNames[1] + "s"
                      : deliveryDayNames.map((n) => n + "s").join(", ")}
                  </strong>.
                </>
              )}
            </Typography>

            {Object.entries(datesByMonth).length === 0 && !loading && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: QUIZ.color.brown,
                    opacity: 0.65,
                  }}
                >
                  No available delivery dates found.
                </Typography>
              </Box>
            )}

            <Stack spacing={2}>
              {Object.entries(datesByMonth).map(([month, dates]) => (
                <Box key={month}>
                  <Typography
                    sx={{
                      fontSize: { xs: 11, md: 12 },
                      fontWeight: 700,
                      color: QUIZ.color.brown,
                      opacity: 0.5,
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {month}
                  </Typography>

                  <Stack spacing={0.5}>
                    {dates.map((d) => {
                      const isSelected = selectedDate === d.date;
                      const isCurrent = currentDeliveryDate === d.date;

                      return (
                        <Box
                          key={d.date}
                          onClick={() => setSelectedDate(d.date)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: { xs: 1.25, md: 1.5 },
                            borderRadius: "10px",
                            border: isSelected
                              ? "2px solid #1A57D3"
                              : "1px solid rgba(0,0,0,.06)",
                            background: isSelected
                              ? "#E6F0FF"
                              : isCurrent
                              ? "#F5F0E8"
                              : QUIZ.color.cream,
                            cursor: "pointer",
                            transition: "all 120ms ease",
                            "&:hover": {
                              borderColor: isSelected
                                ? "#1A57D3"
                                : "rgba(0,0,0,.12)",
                              background: isSelected ? "#E6F0FF" : "#F5F0E8",
                            },
                          }}
                        >
                          <Radio
                            checked={isSelected}
                            size="small"
                            sx={{
                              p: 0,
                              color: "rgba(0,0,0,.2)",
                              "&.Mui-checked": { color: "#1A57D3" },
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: 14, md: 15 },
                                  fontWeight: 700,
                                  color: isSelected ? "#1A57D3" : QUIZ.color.brown,
                                }}
                              >
                                {d.dayOfWeek},{" "}
                                {new Date(
                                  d.date + "T00:00:00Z"
                                ).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  timeZone: "UTC",
                                })}
                              </Typography>
                              {isCurrent && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "#2E7D32",
                                    background: "#E5FFEA",
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: "4px",
                                  }}
                                >
                                  CURRENT
                                </Box>
                              )}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: { xs: 11, md: 12 },
                                color: QUIZ.color.brown,
                                opacity: 0.6,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.25,
                              }}
                            >
                              <LocalShippingIcon sx={{ fontSize: 12 }} />
                              Ships {formatShortDate(d.shipDate)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Box>

      {/* Footer - Sticky */}
      {!success && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          {/* Selected date summary */}
          {hasChangedDate && selectedDateInfo && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "10px",
                background: "#E6F0FF",
                border: "1px solid #C9DFFF",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 12, md: 13 },
                  fontWeight: 700,
                  color: "#1A57D3",
                }}
              >
                New: {formatDateDisplay(selectedDateInfo.date)}
              </Typography>
            </Box>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <QuizButton
              variant="secondary"
              onClick={() => onClose(false)}
              disabled={confirming}
              fullWidth
            >
              Cancel
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={handleConfirm}
              disabled={!hasChangedDate || confirming || loading}
              fullWidth
            >
              {confirming ? "Updating..." : "Confirm Date"}
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
